import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2, MapPin, BookHeart } from 'lucide-react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { shortlistApi } from '../../api/shortlist'

// Gradient combos for avatar backgrounds
const avatarGradients = ['bg-ivory-100 text-maroon-700/40']

function getAvatarGradient(name: string) {
  void name; return avatarGradients[0]
}

export default function ShortlistPage() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['shortlist'],
    queryFn: () => shortlistApi.getAll().then((r) => r.data.data),
  })

  const removeMut = useMutation({
    mutationFn: (profileId: string) => shortlistApi.remove(profileId),
    onSuccess: () => {
      toast.success('Removed from shortlist')
      qc.invalidateQueries({ queryKey: ['shortlist'] })
    },
  })

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center gap-2.5 mb-6">
        <h1 className="font-display font-semibold text-[30px] text-ink-900">Shortlisted</h1>
        {data?.length != null && (
          <span className="text-xs font-semibold bg-ivory-100 text-maroon-700 px-2.5 py-1 rounded-full ring-1 ring-gold-500/40">
            {data.length}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-44 animate-pulse bg-ivory-100 rounded-2xl" />
          ))}
        </div>
      ) : data?.length ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {data.map((entry) => {
            // API returns full ProfileDTO — use entry.id (not profileId), entry.nativeDistrict (not district)
            const initial = entry.fullName?.charAt(0)?.toUpperCase() ?? '?'
            const gradient = getAvatarGradient(entry.fullName ?? '?')
            return (
              <div key={entry.id} className="card rounded-2xl overflow-hidden relative group">
                {/* Remove button */}
                <button
                  onClick={() => removeMut.mutate(entry.id)}
                  className="absolute top-3 right-3 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-ivory-25/90 backdrop-blur-sm text-ink-400 hover:text-danger hover:bg-ivory-25 transition-colors sm:opacity-0 sm:group-hover:opacity-100"
                  title="Remove from shortlist"
                >
                  <Trash2 size={14} />
                </button>

                {/* Whole card opens the profile (Remove button sits above via z-10) */}
                <Link to={`/profile/${entry.id}`} className="block">
                  {/* Avatar area */}
                  <div className={`h-36 mx-2 mt-2 rounded-2xl overflow-hidden flex items-center justify-center ${gradient}`}>
                    <ShortlistPhoto url={entry.avatarUrl} name={entry.fullName} initial={initial} />
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <div className="font-display font-semibold text-ink-900 text-[16px] hover:text-maroon-700 truncate">
                      {entry.fullName ?? 'Profile'}
                    </div>
                    <div className="text-[11px] text-ink-400 flex flex-wrap gap-x-2 mt-0.5">
                      {entry.age > 0 && <span>{entry.age} yrs</span>}
                      {entry.nativeDistrict && (
                        <span className="flex items-center gap-0.5">
                          <MapPin size={10} />
                          {entry.nativeDistrict}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-14 h-14 rounded-full bg-ivory-100 border border-gold-500/30 flex items-center justify-center">
            <BookHeart size={22} strokeWidth={1.5} className="text-maroon-700/50" />
          </div>
          <p className="font-display font-semibold text-lg text-ink-900">No profiles shortlisted yet</p>
          <p className="text-xs text-ink-400">Save profiles you like to revisit them later</p>
          <Link to="/matches" className="btn-primary mt-2 inline-block text-sm">
            Browse Matches
          </Link>
        </div>
      )}
    </div>
  )
}

/** Card photo with the initial as fallback for a missing OR stale (404) URL. */
function ShortlistPhoto({ url, name, initial }: { url?: string | null; name?: string | null; initial: string }) {
  const [broken, setBroken] = useState(false)
  if (!url || broken) return <span className="font-display font-semibold text-3xl">{initial}</span>
  return (
    <div className="relative w-full h-full">
      <img src={url} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-70" />
      <img src={url} alt={name ?? ''} onError={() => setBroken(true)} className="relative w-full h-full object-contain" />
    </div>
  )
}
